import { ComponentFactoryResolver, ComponentRef, Injectable, Injector, ViewContainerRef } from "@angular/core";
import { BehaviorSubject, Subject } from "rxjs";
import { Template } from "ng/src/app/templates/shared/template.model";
import { NoteEntryTocComponent } from "ng/src/app/notes/note-entry-toc/note-entry-toc.component";
import { NoteEntryService } from "ng/src/app/notes/shared/note-entry.service";
import { filter, skip, skipWhile, takeUntil, tap } from "rxjs/operators";
import { TocMode } from "ng/src/app/notes/shared/note-entry.model";
import { NoteControlSource, NoteControlResult, ViewContainerDetails } from './note-controller.model';
import { NoteControlOptions, NoteControlType } from './note-controller-options.model';
import { ILocationService } from "angular";
import { LogBookRootScopeService } from "app/shared/model/app.model";
import { AppStateService } from "ng/src/app/core/services/app-state.service";
import { Utils } from "ng/src/app/core/utils";
import { Note } from "ng/src/app/notes/shared/note.model";
import { LogService } from "ng/src/app/logs/log.service";
import { NoteService } from "ng/src/app/notes/shared/note.service";
import { PrimeNGHelpersService } from "ng/src/app/core/services/primeng-helpers.service";
import { Log } from "ng/src/app/logs/log.model";
import { HelpSection } from "ng/src/app/core/help-section";

@Injectable({ providedIn: 'root' })
export class NoteControlService extends NoteControlSource {
	
	readonly barWidth = 500;
	
	private readonly notePublishedSubject = new Subject();
	readonly notePublished$ = this.notePublishedSubject.asObservable();
	
	private readonly enterNoteDetailsSubject = new BehaviorSubject<ViewContainerDetails | null>(null);
	
	get enterNotesDetails() {
		return this.enterNoteDetailsSubject.value;
	}
	set enterNotesDetails(val) {
		this.enterNoteDetailsSubject.next(val);
	}
	
	private _enterNotesContainer?: ViewContainerRef;
	get enterNotesContainer() {
		return this._enterNotesContainer;
	}
	set enterNotesContainer(val) {
		this._enterNotesContainer = val;
		this.updateEnterNotesContainer();
	}
	
	
	private _currentWidth = 0;
	get currentWidth() {
		return this._currentWidth;
	}
	set currentWidth(val) {
		this._currentWidth = val;
		this.updateHiddenEntries()
	}
	
	get $location() {
		return this.injector.get('$location') as ILocationService;
	}
	
	get $rootScope() {
		return this.injector.get('$rootScope') as LogBookRootScopeService;
	}
	
	constructor(
		private componentFactory: ComponentFactoryResolver,
		private noteService: NoteService,
		private primengHelpers: PrimeNGHelpersService,
		private injector: Injector
	) {
		super();
		
		LogBookRootScopeService.observeEvent(this.$rootScope, '$locationChangeSuccess').pipe(
			filter(([event, newUrl]) => !this.isEnterNotesUrl() && !!this.enterNotesContainer)
		).subscribe(() => {
			this.enterNotesContainer = undefined;
		});
		
		LogBookRootScopeService.observeEvent(this.$rootScope, 'logoutStart')
			.pipe(filter(() => !!AppStateService.currentUser))
			.subscribe(([event, logout]) => {
				const all = this.getAllCurrentContainers();
				const detailsWithWork = all.find(c => c.hasWork());
				if (detailsWithWork) {
					event.preventDefault();
					detailsWithWork.component.instance.stopEditingNote(() => {
						this.getAllCurrentContainers().forEach(c => this.removeEntry(c));
						logout();
					})
				}
			});
		
		LogBookRootScopeService.observeEvent(this.$rootScope, 'helpOpening')
			.pipe(filter(() => {
				return !!this.enterNotesContainer?.get(0) || this.popup.length > 0 || this.bar.some(e => e.barSettings.maximized);
			}))
			.subscribe(([event, help]) => {
				help.setSection('note-entry-editor', HelpSection.Priority.HIGH);
			})
		
		this.update$.subscribe(() => {
			if (this.enterNotesDetails && this._enterNotesContainer && this.enterNotesDetails.container !== this._enterNotesContainer) {
				this.enterNotesDetails = null;
			}
		})
	}
	
	startAndRegister(startWith: Template | Note | null, container: ViewContainerRef, sectionId?: number): ViewContainerDetails {
		const details = this.createContainerDetails(startWith, sectionId);
		details.container = container;
		
		return details;
	}
	
	start(startWith: Template | null, type?: NoteControlType): NoteControlResult;
	start(startWith: Note, type?: NoteControlType, sectionId?: number): NoteControlResult;
	start(startWith: Template | Note | null, type?: NoteControlType, sectionId?: number): NoteControlResult {
		const details = this.createContainerDetails(startWith, sectionId);
		
		const noInitial = type == null;
		if (noInitial) {
			type = NoteControlOptions.validateOption(AppStateService.currentUser?.config?.defaultNoteEntry) ??
				NoteControlOptions.validateOption(LogService.getConfigValueFor('defaultNoteEntry')) ??
				'ENTER_NOTES';
			
			if (type === 'BAR' && this.currentWidth < this.barWidth)
				type = 'DIALOG';
		}
		
		details.component.instance.noteEntryService.viewIsNarrow = type !== 'ENTER_NOTES';
		
		switch (type) {
			case 'BAR':
				this.sendToBar(details, false, 'end');
				break;
			case 'FULLSCREEN':
			case 'DIALOG':
				if (type === 'FULLSCREEN')
					details.dialogSettings.maximized = true;
				
				this.sendToDialog(details);
				break;
			default:
				this.sendToEnterNotes(details);
		}
		
		return {
			destroy: details.component.instance.onDestroy$,
			notePublished: details.component.instance.notePublished,
			accessRevoked: details.component.instance.onAccessRevoked,
			tocMode: details.component.instance.mode$
		}
	}
	
	
	sendToBar(details: ViewContainerDetails, minimized = false, position: 'start' | 'end' = 'start') {
		details.barSettings.maximized = !minimized;
		
		this.move(details, 'bar', position);
		this.updateHiddenEntries();
	}
	
	sendToDialog(details: ViewContainerDetails) {
		details.dialogSettings.maximized = this.shouldUseMobileView() || details.dialogSettings.maximized;
		this.move(details, 'popup', 'end');
	}
	
	sendToEnterNotes(details: ViewContainerDetails) {
		if (!this.isEnterNotesUrl())
			this.$location.path('/notes');
		
		const previous = this.enterNotesDetails;
		if (previous) {
			if (previous.hasWork()) {
				this.sendToBar(previous, true);
			}
			else {
				this.removeEntry(previous);
			}
		}
		
		Utils.let(this.find(details), (found) => {
			if (!found) return;
			details.removeFromContainer();
			found.src.splice(found.index, 1);
		})
		
		this.enterNotesDetails = details;
		details.container = this.enterNotesContainer;
	}
	
	destroy(entry: ComponentRef<NoteEntryTocComponent> | ViewContainerDetails) {
		if ('component' in entry) {
			entry = entry.component;
		}
		
		entry.instance.stopEditingNote(() => this.removeEntry(entry));
		
		this.updateHiddenEntries();
	}
	
	updateHiddenEntries() {
		const allowedEntries = this.getAllowedDockedEntryCount();
		
		if (allowedEntries > this.bar.length && this.hidden.length) {
			const countToUnhide = allowedEntries - this.bar.length;
			if (countToUnhide <= 0) return;
			this.bar.splice(0, 0, ...this.hidden.splice(this.hidden.length - 1 - countToUnhide) );
		}
		else if (allowedEntries < this.bar.length) {
			const overflow = (this.bar.length - allowedEntries);
			if (overflow < 0) return;
			
			const removed = this.bar.splice(this.bar.length - overflow);
			removed.forEach(detail => detail.removeFromContainer());
			this.hidden.push(...removed);
		}
	}
	
	unhideEntry(idx: number) {
		const entry = this.hidden.splice(idx, 1)[0];
		if (!entry) return;
		
		const viewCanContainerEntries = this.getAllowedDockedEntryCount() > 0;
		if (viewCanContainerEntries) {
			this.sendToBar(entry, false, 'start');
		}
		else {
			this.sendToDialog(entry);
		}
	}
	
	getAllCurrentContainers(): ViewContainerDetails[] {
		const containers = (Object.values(this.getSrcMap()) as ViewContainerDetails[][])
			.reduce((containers, current) => {
				return containers.concat(current);
			}, [] as ViewContainerDetails[]);
		
		if (this.enterNotesDetails)
			containers.push(this.enterNotesDetails);
		
		return containers;
	}
	
	private getAllowedDockedEntryCount() {
		// Take the button into account when getting available space
		const availableWidth = this.currentWidth - (Utils.convertRemToPixels(2.357) + 5);
		return Math.floor(availableWidth / this.barWidth);
	}
	
	private createContainerDetails(startWith: Template | Note | null, sectionId?: number): ViewContainerDetails {
		let templateId: number | undefined = undefined;
		let note: Note | undefined = undefined;
		let logId: number = -1;
		
		if (startWith && 'note' in startWith) {
			templateId = startWith.templateId;
			logId = startWith.logId;
			note = startWith;
		}
		else if (startWith) {
			templateId = startWith.id;
			logId = startWith.logId;
		}
		
		let details: ViewContainerDetails | undefined;
		
		if (note) {
			// If the editing note already exists in the current managed instances, bring it back to the front
			const noteId = note.id;
			details = this.getAllCurrentContainers().find(details => {
				const editingId = details.component.instance.noteEntryService.editingNote?.id;
				return typeof editingId === 'number' && editingId === noteId;
			});
		}
		
		if (!details) {
			const factory = this.componentFactory.resolveComponentFactory(NoteEntryTocComponent);
			
			const component = factory.create(this.injector);
			details = new ViewContainerDetails(component);
			
			const service: NoteEntryService = component.instance.noteEntryService;
			
			component.instance.startConfig = { disableLocationModification: true };
			if (logId > 0) {
				component.instance.startConfig.selectedLog = logId;
			}
			component.instance.trapOnNavigation = false;
			component.onDestroy(() => this.removeEntry(component));
			
			component.instance.notePublished.pipe(
				takeUntil(component.instance.onDestroy$)
			).subscribe(note => {
				let publishedLog: Log | undefined = service.selectedLog;
				
				this.notePublishedSubject.next();
				if (note) {
					this.noteService.next(note.id);
					let additionalLogText = '';
					if (publishedLog && LogService.selectedLog && publishedLog.id === note.logId &&
						publishedLog.id !== LogService.selectedLog.id) {
						additionalLogText = ` to ${publishedLog.name}`;
					}
					this.primengHelpers.ajsSuccessToast(`${Note.getNoteName(note) ?? 'Basic Note'} has been saved${additionalLogText}.`);
				}
			});
			
			component.instance.mode$.pipe(
				skipWhile(() => !details?.container),
				filter((mode) => mode === TocMode.NAVIGATOR),
				skip(1),
				takeUntil(component.instance.onDestroy$.pipe(
					tap(() => this.removeEntry(component)))
				))
				.subscribe(() => this.removeEntry(component));
			
			service.isReady$.pipe(
					filter(ready => ready)
				)
				.subscribe(() => {
					if (note) {
						service.editNoteId(note.id);
					}
					else if (templateId) {
						service.editTemplate(templateId);
					}
				});
		}
		
		if (typeof sectionId === 'number') {
			details.component.instance.noteEntryService.editSectionId(sectionId);
		}
		
		this.move(details, 'other');
		
		return details;
	}
	
	shouldUseMobileView() {
		return Utils.isMobile || !AppStateService.hoverSupported;
	}
	
	private updateEnterNotesContainer() {
		if (!this.enterNotesDetails) return;
		
		const newContainer = this._enterNotesContainer;
		if (!newContainer) {
			if (this.enterNotesDetails.hasWork()) {
				this.sendToBar(this.enterNotesDetails, true);
			}
			else {
				this.removeEntry(this.enterNotesDetails);
			}
			
			this.enterNotesDetails = null;
		}
		else {
			this.enterNotesDetails.container = newContainer;
		}
	}
	
	private removeEntry(entry: ViewContainerDetails | ComponentRef<NoteEntryTocComponent>) {
		const component = ('component' in entry) ? entry.component : entry;
		
		this.remove(entry => entry.component === component)
			.forEach(removedItem => {
				removedItem.container?.clear();
			});
		
		if (this.enterNotesDetails?.component && this.enterNotesDetails?.component === component) {
			this.enterNotesDetails.component.destroy();
			this.enterNotesDetails = null;
		}
	}
	
	private isEnterNotesUrl() {
		const currentPath = this.$location.path();
		const validPaths = ['/notes', '/recent-notes'];
		return validPaths.some(path => path === currentPath);
	}
}